/**
 * PhysicsEventEmitter (Wave 1.2, 2026-05-15) — hot-path-safe ring of
 * physics trace events. Goal: tag every named physics event (snap-to-ground,
 * collision correction, jump start) so frame-jitter spikes can be attributed
 * to specific behaviors without parsing the gameLogger log stream.
 *
 * Allocation discipline (this is called at 60 FPS × multiple subsystems):
 *   - Ring buffer of pre-allocated event objects. emit() writes in place.
 *   - Caller passes primitives only; no destructuring, no spread.
 *   - drain() returns a shallow copy so the producer can keep filling.
 *
 * Use:
 *   import { physicsEventEmitter } from '@/game/diagnostics/PhysicsEventEmitter';
 *   physicsEventEmitter.emit('snap_to_ground', entityId, x, y, NaN, NaN, gap);
 *
 * The PhysicsTraceReporter mounts a 5s interval that drains the emitter
 * and pushes via Colyseus `netobs:physicsTrace`. Server-side caching in
 * PhysicsTraceBuffer; MCP fetches via `/api/diag/physics-trace`.
 */

import type {
  PhysicsEventKind,
  PhysicsEventWire,
} from '../../shared/diagnostics/PhysicsTraceTypes';

const RING_CAPACITY = 512;

export class PhysicsEventEmitter {
  /** Pre-allocated ring. Each slot is mutated in-place by emit(). */
  private readonly ring: PhysicsEventWire[];
  private writeIdx = 0;
  private filled = 0;
  private emittedSinceDrain = 0;
  private droppedSinceDrain = 0;

  constructor() {
    this.ring = new Array(RING_CAPACITY);
    for (let i = 0; i < RING_CAPACITY; i++) {
      this.ring[i] = { ts: 0, kind: 'snap_to_ground', entityId: '', x: 0, y: 0, vx: 0, vy: 0, detail: 0 };
    }
  }

  emit(
    kind: PhysicsEventKind,
    entityId: string,
    x: number,
    y: number,
    vx: number,
    vy: number,
    detail: number,
  ): void {
    const slot = this.ring[this.writeIdx];
    slot.ts = Date.now();
    slot.kind = kind;
    slot.entityId = entityId;
    slot.x = x;
    slot.y = y;
    slot.vx = vx;
    slot.vy = vy;
    slot.detail = detail;
    this.writeIdx = (this.writeIdx + 1) % RING_CAPACITY;
    if (this.filled < RING_CAPACITY) this.filled++;
    else this.droppedSinceDrain++;
    this.emittedSinceDrain++;
  }

  /**
   * Snapshot the current ring contents (chronological order, oldest first)
   * and reset overflow counters. Caller takes ownership of the returned
   * array; the ring itself is unaffected by this drain (subsequent emits
   * will overwrite from the current write index).
   */
  drain(): { events: PhysicsEventWire[]; emitted: number; dropped: number } {
    const events: PhysicsEventWire[] = new Array(this.filled);
    const start = this.filled === RING_CAPACITY ? this.writeIdx : 0;
    for (let i = 0; i < this.filled; i++) {
      const src = this.ring[(start + i) % RING_CAPACITY];
      // Defensive copy — caller may serialize this asynchronously.
      events[i] = {
        ts: src.ts,
        kind: src.kind,
        entityId: src.entityId,
        x: src.x,
        y: src.y,
        vx: src.vx,
        vy: src.vy,
        detail: src.detail,
      };
    }
    const emitted = this.emittedSinceDrain;
    const dropped = this.droppedSinceDrain;
    this.writeIdx = 0;
    this.filled = 0;
    this.emittedSinceDrain = 0;
    this.droppedSinceDrain = 0;
    return { events, emitted, dropped };
  }
}

/** Module-level singleton — small enough to live on the heap forever. */
export const physicsEventEmitter = new PhysicsEventEmitter();
